import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Check, Mail, ChevronDown } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: "investor", label: "I am an investor" },
  { value: "owner", label: "I own infrastructure assets" },
  { value: "municipality", label: "I represent a municipality" },
  { value: "partnership", label: "I am interested in partnerships" },
];

export default function Waitlist() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("investor");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        role,
        source: "early_access_form",
      });
      setDone(true);
      toast.success("You're on the list. We'll be in touch.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please enter a valid email.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="early"
      className="relative mx-auto max-w-3xl px-6 py-24 text-center lg:px-8 lg:py-32"
    >
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
      >
        <p className="mb-4 font-display text-[12px] font-extrabold uppercase tracking-[0.18em] text-[#38BDF8]">
          Early Access
        </p>
        <h2 className="font-display text-3xl font-bold leading-[1] tracking-[-0.04em] text-[#F8FAFC] sm:text-5xl lg:text-[58px]">
          Join the InfraYield launch list.
        </h2>
        <p className="mx-auto mt-6 max-w-xl font-body text-base leading-[1.7] text-[#CBD5E1] sm:text-lg">
          Be first to receive investor updates, owner tools, and municipal
          partnership announcements.
        </p>

        <form
          onSubmit={handleSubmit}
          className="mx-auto mt-10 grid w-full max-w-2xl grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]"
          data-testid="waitlist-form"
        >          <div className="relative">
            <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#475569]" />
            <input
              type="email"
              required
              value={email}
              disabled={submitting || done}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email address"
              className="w-full rounded-md border border-[#1E293B] bg-[#0F1B2D] py-3.5 pl-11 pr-4 font-body text-sm text-[#F8FAFC] placeholder:text-[#475569] focus:border-[#38BDF8] focus:outline-none focus:ring-1 focus:ring-[#38BDF8] disabled:opacity-60"
              data-testid="waitlist-email-input"
            />
          </div>

          <div className="relative">
            <select
              value={role}
              disabled={submitting || done}
              onChange={(e) => setRole(e.target.value)}
              className="w-full appearance-none rounded-md border border-[#1E293B] bg-[#0F1B2D] py-3.5 pl-4 pr-10 font-body text-sm text-[#F8FAFC] focus:border-[#38BDF8] focus:outline-none focus:ring-1 focus:ring-[#38BDF8] disabled:opacity-60"
              data-testid="waitlist-role-select"
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value} className="bg-[#0F1B2D]">
                  {r.label}
                </option>
              ))}
            </select>
            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
          </div>

          <button
            type="submit"
            disabled={submitting || done}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#22C55E] px-6 py-3.5 font-body text-sm font-semibold text-[#07111F] transition-all hover:bg-[#22C55E]/90 disabled:opacity-70"
            data-testid="waitlist-submit"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : done ? (
              <>
                <Check className="h-4 w-4" />
                Joined
              </>
            ) : (
              "Join Early Access"
            )}
          </button>
        </form>

        <p className="mt-5 font-body text-xs text-[#64748B]">
          We'll only email you about InfraYield. Unsubscribe anytime.
        </p>
      </motion.div>
    </section>
  );
}
